import "./TriviaCreateForm.scss";

import { Alert, Button, Flex, Skeleton, Text } from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import {
  AuthoringFormTrackingFields,
  ISO8601Duration,
  TeamsQuestionTemplateDifficulty,
} from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useTeamsDetails } from "../../../../_shared/actions/useTeamsDetails";
import { DropdownField } from "../../../../_shared/components/fields";
import { FetchError, isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { templatesApi } from "../../../../_shared/services/TemplatesApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { getAudienceFromTeamsContext } from "../../../../_shared/utils/Authoring";
import { AuthoringFormHeader } from "../../SurveyAuthoring/AuthoringFormHeader";
import { useTrackingProperties } from "../../SurveyAuthoring/PollyAuthoring/hooks/useTrackingProperties";
import { AuthoringFormHeaderOptions } from "../../SurveyAuthoring/PollySurveyAuthoringForm";
import { InstallWarning } from "../InstallWarning";

type Props = {
  onCreated: (survey: SurveyDTO) => void;
  trackingFields?: AuthoringFormTrackingFields;
  headerOptions?:
    | {
        showHeader?: false;
      }
    | ({
        showHeader: true;
      } & AuthoringFormHeaderOptions);
};

export const TriviaCreateForm: React.FC<Props> = props => {
  const { onCreated, headerOptions, trackingFields } = props;
  const { inActiveMeeting, groupType, hostClientType, meetingType } = useTrackingProperties({
    isPersonal: trackingFields?.isPersonal ?? false,
  });

  const { teamsContext } = useTeamsContext();
  const [categories, setCategories] = useState<string[]>();
  const { isTeamsDetailsLoading, loadTeamsDetails } = useTeamsDetails();

  useEffect(() => {
    loadTeamsDetails(teamsContext);
  }, [loadTeamsDetails, teamsContext]);

  useEffect(() => {
    (async () => {
      const results = await templatesApi.getQuestionTemplateCatgories(teamsContext, "trivia");
      if (isError(results)) {
        console.error(results);
      } else {
        setCategories(results.categories);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isTeamsDetailsLoading) {
    return (
      <Skeleton animation="wave" className="polly-authoring-page">
        <Flex column gap="gap.medium" hAlign="center">
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line width="50%" />
          <Skeleton.Line height="2px" />
          <Skeleton.Button width="100%" />
        </Flex>
      </Skeleton>
    );
  }

  if (!teamsContext.meeting) {
    return <InstallWarning type="trivia" />;
  }

  const submit = async (form: FormData<FormFields>) => {
    const { fields } = form;

    const audienceItem = getAudienceFromTeamsContext(teamsContext)!;

    if (!audienceItem) {
      return [{ message: "Oops, something went wrong while creating the trivia." }];
    }

    const submitResponse = await surveyApi.createTrivia(
      teamsContext,
      {
        numberOfQuestions: fields.numberOfQuestion.value,
        category: fields.category.value as string,
        difficulty: fields.difficulty.value,
        questionDuration: fields.gameSpeed.value,
        recipient: {
          id: audienceItem.id,
          groupId: audienceItem.groupId,
          type: audienceItem.type,
          name: audienceItem.name,
        },
      },
      {
        authoringOrigin: trackingFields?.authoringOrigin,
        trackingSource: trackingFields?.trackingSource,
        groupType,
        hostClientType,
        inActiveMeeting,
        meetingType,
      }
    );

    if (isError(submitResponse)) {
      if (submitResponse instanceof FetchError) {
        return [{ message: submitResponse.body?.message as string }];
      }
      return [{ message: submitResponse.message }];
    }

    onCreated(submitResponse.survey);
  };

  return (
    <>
      {headerOptions?.showHeader && (
        <AuthoringFormHeader
          isEdit={headerOptions.isEdit}
          templateId={headerOptions.templateId}
          trackingSource={headerOptions.trackingSource}
        />
      )}

      <Flex column gap="gap.medium" hAlign="center">
        <Text content="Create Trivia Game" weight="bold" size="large" />

        <FormState initialValues={DEFAULT_VALUES} onSubmit={submit}>
          {({ fields, submit, submitting, errors }) => {
            return (
              <Flex column gap="gap.medium" className="trivia-create-form" hAlign="stretch">
                <DropdownField
                  field={fields.category}
                  fluid
                  items={
                    categories
                      ? categories.map(c => ({ label: c.replace(":", " > "), value: c }))
                      : []
                  }
                  label="📂 Category"
                  loading={!categories}
                />
                <DropdownField
                  field={fields.difficulty}
                  fluid
                  items={[
                    {
                      label: "Easy",
                      value: "easy",
                    },
                    {
                      label: "Medium",
                      value: "medium",
                    },
                    {
                      label: "Hard",
                      value: "hard",
                    },
                  ]}
                  label="🤔 Question difficulty"
                />
                <DropdownField
                  field={fields.numberOfQuestion}
                  fluid
                  items={[
                    {
                      label: "5 questions",
                      value: 5,
                    },
                    {
                      label: "10 questions",
                      value: 10,
                    },
                    {
                      label: "15 questions",
                      value: 15,
                    },
                    {
                      label: "20 questions",
                      value: 20,
                    },
                  ]}
                  label="💬 Number of questions"
                />
                <DropdownField
                  field={fields.gameSpeed}
                  fluid
                  label="⏲️ Game speed"
                  items={[
                    {
                      label: "10 seconds per question",
                      value: "PT10S",
                    },
                    {
                      label: "15 seconds per question",
                      value: "PT15S",
                    },
                    {
                      label: "20 seconds per question",
                      value: "PT20S",
                    },
                    {
                      label: "30 seconds per question",
                      value: "PT30S",
                    },
                    {
                      label: "45 seconds per question",
                      value: "PT45S",
                    },
                    {
                      label: "60 seconds per question",
                      value: "PT1M",
                    },
                  ]}
                />

                {errors?.length > 0 && errors.map(e => <Alert danger content={e.message} />)}

                <Flex hAlign="end" style={{ marginTop: "1rem" }} gap="gap.small">
                  <Button
                    content="Save"
                    primary
                    onClick={submit}
                    loading={submitting}
                    disabled={submitting || !fields.category.value}
                  />
                </Flex>
              </Flex>
            );
          }}
        </FormState>
      </Flex>
    </>
  );
};

const DEFAULT_VALUES: FormFields = {
  category: "Animals:General",
  numberOfQuestion: 10,
  difficulty: "medium",
  gameSpeed: "PT30S",
};

type FormFields = {
  category: string | undefined;
  numberOfQuestion: number;
  gameSpeed: ISO8601Duration;
  difficulty: TeamsQuestionTemplateDifficulty;
};
